/**
 * =============================================================================
 * SEO — Componente de metadados
 * =============================================================================
 *
 * Injeta <title>, meta tags, Open Graph, Twitter Cards, canonical e
 * Structured Data (Schema.org) no <head> da página.
 *
 * Usa react-helmet-async para gestão segura do <head> em SPA.
 */

import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  buildCanonicalUrl,
  DEFAULT_OG_IMAGE,
  LOCALE,
  SITE_NAME,
  SITE_URL,
  type PageSeo,
} from "@/config/seo";

export interface SeoProps extends Partial<PageSeo> {
  /** Caminho canónico alternativo (ex: "/curriculo"). Se omitido, usa o pathname atual. */
  canonicalPath?: string;
}

export function Seo({
  title,
  description,
  ogType = "website",
  ogImage = DEFAULT_OG_IMAGE,
  keywords,
  noindex = false,
  schema,
  canonicalPath,
}: SeoProps) {
  const location = useLocation();
  const canonicalUrl = canonicalPath
    ? `${SITE_URL}${canonicalPath}`
    : buildCanonicalUrl(location.pathname);

  const pageTitle = title ?? SITE_NAME;
  const fullOgImage = ogImage?.startsWith("http")
    ? ogImage
    : `${SITE_URL}${ogImage}`;

  const robots = noindex ? "noindex, nofollow" : "index, follow";

  const jsonLd = schema ? buildJsonLd(schema) : null;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={description ?? ""} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description ?? ""} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={LOCALE} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description ?? ""} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={pageTitle} />
      <meta name="twitter:domain" content={SITE_URL.replace(/^https?:\/\//, "")} />

      {/* Schema.org */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

function buildJsonLd(schema: PageSeo["schema"]) {
  if (Array.isArray(schema)) {
    return {
      "@context": "https://schema.org",
      "@graph": schema,
    };
  }
  return schema;
}
